import {
  adjustToTimeZone,
  Dispatch,
  DispatchPatchDTO,
  dispatchStatus,
  useCreateDispatchMutation,
  useDeleteDispatchMutation,
  usePatchDispatchMutation,
  useUpdateDispatchesMutation,
  useUpdateDispatchMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {useAuth} from "../providers/AuthProvider/AuthProvider";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseDispatchMutationProps = {}

export type UseDispatchMutationResult = {
  createDispatch: (dispatch: Partial<Dispatch>, onSuccess: ((dispatch: Dispatch) => void) | undefined) => void
  updateDispatch: (dispatch: Partial<Dispatch>, callback: EntityMutationCallback | undefined) => void
  patchDispatch: (dispatchId: string, patch: DispatchPatchDTO, callback: EntityMutationCallback | undefined) => void
  updateDispatches: (dispatches: Partial<Dispatch>[], callback: EntityMutationCallback | undefined) => void
  cancelDispatch: (dispatch: Partial<Dispatch>, comments: string, callback: EntityMutationCallback | undefined) => void
  deleteDispatch: (dispatchId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useDispatchMutation = (props: UseDispatchMutationProps = {}): UseDispatchMutationResult => {
  const {t} = useTranslation(["common"])

  const {userId} = useAuth()

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createDispatch, {isLoading: isCreating}] = useCreateDispatchMutation()
  const [_updateDispatch, {isLoading: isUpdating}] = useUpdateDispatchMutation()
  const [_patchDispatch, {isLoading: isPatching}] = usePatchDispatchMutation()
  const [_updateDispatches, {isLoading: isUpdatingMulti}] = useUpdateDispatchesMutation()
  const [_deleteDispatch, {isLoading: isDeleting}] = useDeleteDispatchMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating || isUpdatingMulti || isPatching)
  }, [isUpdating, isUpdatingMulti, isPatching, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useDispatchUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.dispatch", "Dispatch"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useDispatchUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.dispatch", "Dispatch"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useDispatchUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.dispatch", "Dispatch"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDispatchUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Dispatch>, onSuccess: ((dispatch: Dispatch) => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.dispatch", "Dispatch") + " " + t("common.status.created", "Created"),
          message: "The driver will be notified of the new job."
        });
        if (onSuccess) {
          onSuccess(payload)
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.dispatch", "Dispatch") + " " + t("common.status.updated", "Updated"),
          message: "The driver will be notified of the updated job."
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })
          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.dispatch", "Dispatch") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((dispatchId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteDispatch({id: dispatchId, deviceId: "0", authentication: userId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteDispatch, handleDeleteResult, userId])

  const createDispatch = useCallback((dispatch: Partial<Dispatch>, onSuccess: ((dispatch: Dispatch) => void) | undefined) => {
    const promise = _createDispatch({...dispatch, authentication: userId}).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createDispatch, handleCreateResult, userId])

  const updateDispatch = useCallback((dispatch: Partial<Dispatch>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateDispatch({...dispatch, authentication: userId}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateDispatch, handleUpdateResult, userId])

  const patchDispatch = useCallback((dispatchId: string, patch: DispatchPatchDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _patchDispatch({
      id: dispatchId,
      patch: patch,
    }).unwrap()
    handleUpdateResult(promise, callback)
  }, [_patchDispatch, handleUpdateResult])

  const updateDispatches = useCallback((dispatches: Partial<Dispatch>[], callback: EntityMutationCallback | undefined) => {
    const promise = _updateDispatches([...dispatches]).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateDispatches, handleUpdateResult])

  const cancelDispatch = useCallback((dispatch: Partial<Dispatch>, comments: string, callback: EntityMutationCallback | undefined) => {
    const promise = _updateDispatch({
      ...dispatch,
      status: dispatchStatus.canceled.code,
      lastModifiedDttm: adjustToTimeZone().getTime(),
      comments: comments,
    }).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateDispatch, handleUpdateResult])

  const deleteDispatch = useCallback((dispatchId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.dispatch", "Dispatch"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletedispatch.confirm", "Are you sure you want to delete this dispatch?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(dispatchId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createDispatch: createDispatch,
    updateDispatch: updateDispatch,
    patchDispatch: patchDispatch,
    updateDispatches: updateDispatches,
    cancelDispatch: cancelDispatch,
    deleteDispatch: deleteDispatch,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
